import type { IChartApi, MouseEventParams, Time } from "lightweight-charts"

// A label at a chart marker while the pointer is on it: a trade's or fill's side, size and price
// (docs/design/0016). Markers carry an id; the chart reports the hovered one and the label for
// that id shows next to the pointer. Plain TypeScript; the layer owning the markers disposes it.

export class MarkerTips {
  private readonly chart: IChartApi
  private readonly tip: HTMLDivElement
  private texts: ReadonlyMap<string, string> = new Map()
  private readonly onMove = (param: MouseEventParams<Time>) => this.move(param)

  constructor(chart: IChartApi) {
    this.chart = chart
    this.tip = document.createElement("div")
    this.tip.className = "marker-tip"
    this.tip.setAttribute("role", "tooltip")
    this.tip.hidden = true
    document.body.appendChild(this.tip)
    chart.subscribeCrosshairMove(this.onMove)
  }

  /** The label of each marker id (markers without one show none). */
  set(texts: ReadonlyMap<string, string>): void {
    this.texts = texts
    if (texts.size === 0) this.tip.hidden = true
  }

  dispose(): void {
    this.chart.unsubscribeCrosshairMove(this.onMove)
    this.tip.remove()
  }

  private move(param: MouseEventParams<Time>): void {
    const id = param.hoveredObjectId
    const text = typeof id === "string" ? this.texts.get(id) : undefined
    if (text === undefined || param.point === undefined) {
      this.tip.hidden = true
      return
    }
    const rect = this.chart.chartElement().getBoundingClientRect()
    this.tip.textContent = text
    this.tip.hidden = false
    // Right of the pointer, or left of it near the chart's right edge.
    const x = rect.left + param.point.x
    const width = this.tip.offsetWidth
    const left = x + 14 + width > rect.right ? x - 14 - width : x + 14
    this.tip.style.left = `${Math.max(rect.left, left)}px`
    this.tip.style.top = `${rect.top + param.point.y - this.tip.offsetHeight - 10}px`
  }
}
